/* eslint-disable no-plusplus */
import Hidden from '@material-ui/core/Hidden';
import { makeStyles, ThemeProvider, useTheme } from '@material-ui/core/styles';
import NavbarMobileToggleFab from 'app/fuse-layouts/shared-components/NavbarMobileToggleFab';
import * as Actions from 'app/store/actions';
import clsx from 'clsx';
import React, { useRef, useEffect, useMemo } from 'react';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import { useDispatch, useSelector } from 'react-redux';
import { usePrevious } from 'react-use';
import Icon from 'components/Icon';
import NavbarLayout1 from './NavbarLayout1';

const navbarWidth = 245;
const TOGGLE_CLICK_WAIT_MS = 300;
const KEEP_COLLAPSED = 'Keep collapsed';
const EXPAND_ON_HOVER = 'Expand on hover';
const ARROW_MOTION = '680ms cubic-bezier(0.16, 1, 0.3, 1)';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 4,
    overflow: 'visible',
    [theme.breakpoints.up('xs')]: {
      width: navbarWidth,
      minWidth: navbarWidth,
    },
  },
  wrapperFolded: {
    [theme.breakpoints.up('xs')]: {
      width: 64,
      minWidth: 64,
    },
  },
  navbar: {
    display: 'flex',
    overflow: 'visible',
    flexDirection: 'column',
    flex: '1 1 auto',
    width: navbarWidth,
    minWidth: navbarWidth,
    height: '100%',
    zIndex: 4,
    transition: theme.transitions.create(['width', 'min-width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shorter,
    }),
    // boxShadow: theme.shadows[3]
  },
  left: {
    left: 0,
  },
  right: {
    right: 0,
  },
  folded: {
    position: 'absolute',
    width: 64,
    minWidth: 64,
    top: 0,
    bottom: 0,
    overflow: 'visible',
  },
  foldedAndOpened: {
    width: `${navbarWidth}px!important`,
    minWidth: `${navbarWidth}px!important`,
  },
  navbarContent: {
    flex: '1 1 auto',
  },
  foldedAndClosed: {
    '& $navbarContent': {
      '& .logo-icon': {
        width: 32,
        height: 32,
      },
      '& .logo-text': {
        opacity: 0,
      },
      '& .react-badge': {
        opacity: 0,
      },
      '& .item-badge': {
        opacity: 0,
        visibility: 'hidden',
      },
      '& .list-subheader .list-subheader-text': {
        opacity: 0,
      },
      '& .list-subheader:before': {
        content: '""',
        display: 'block',
        position: 'absolute',
        minWidth: 16,
        borderTop: '2px solid',
        opacity: 0.2,
      },
      '& .collapse-children': {
        display: 'none',
      },
      '& .user': {
        '& .username, & .email': {
          opacity: 0,
        },
        '& .avatar': {
          width: 40,
          height: 40,
          top: 32,
          padding: 0,
        },
      },
      '& .list-item.active': {
        marginLeft: 6,
        width: 50,
        padding: 15,
        borderRadius: '25px',
        transition: theme.transitions.create(['width'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.shorter,
        }),
        '&.square': {
          borderRadius: 0,
          marginLeft: 0,
          paddingLeft: 24,
          width: '100%',
        },
      },
    },
  },
  edgeToggle: {
    position: 'absolute',
    top: '32px',
    right: 0,
    transform: 'translateX(50%) translateY(50%)',
    zIndex: 20,
    width: 28,
    height: 28,
    minWidth: 28,
    padding: 0,
    margin: 0,
    border: 0,
    borderRadius: '50%',
    appearance: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    cursor: 'pointer',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.35)',
    outline: 'none',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    '&:focus-visible': {
      boxShadow: `0 0 0 2px ${theme.palette.common.white}, 0 0 0 4px ${theme.palette.primary.main}`,
    },
  },
  toggleSpin: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transformOrigin: 'center center',
    transition: `transform ${ARROW_MOTION}`,
  },
  toggleIconCollapsed: {
    transform: 'rotate(180deg)',
  },
  toggleIconExpanded: {
    transform: 'rotate(0deg)',
  },
  arrowGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transformOrigin: 'center center',
    transition: `transform ${ARROW_MOTION}`,
  },
  arrowGroupDouble: {
    transform: 'scale(0.72)',
  },
  arrowExtra: {
    display: 'inline-flex',
    alignItems: 'center',
    maxWidth: 0,
    opacity: 0,
    overflow: 'hidden',
    marginLeft: 0,
    transition: `max-width ${ARROW_MOTION}, margin-left ${ARROW_MOTION}, opacity 520ms ease`,
  },
  arrowIcon: {
    stroke: '#ffffff',
    strokeWidth: '42',
    strokeLinejoin: 'round',
    paintOrder: 'stroke fill',
  },
  arrowExtraVisible: {
    maxWidth: '0.85em',
    opacity: 1,
    marginLeft: '-0.3em',
  },
  collapseItemText: {
    '& $navbarContent': {
      '& .list-item-text': {
        marginLeft: 8,
      },
    },
  },
  noneCollapse: {
    '& $navbarContent': {
      '& .list-item-text': {
        marginLeft: 8,
      },
    },
  },
}));

function NavbarWrapperLayout1() {
  const dispatch = useDispatch();
  const config = useSelector(({ fuse }) => fuse.settings.current.layout.config);
  const navbarTheme = useSelector(({ fuse }) => fuse.settings.navbarTheme);
  const navbar = useSelector(({ fuse }) => fuse.navbar);
  const navBarStateRef = useRef(navbar);
  const navRef = useRef(null);
  const childNavRef = useRef(null);
  const hoverOpenRef = useRef(false);
  const prevNavbar = usePrevious(navbar);
  const theme = useTheme();

  const classes = useStyles();
  const { folded } = config.navbar;
  const foldedAndClosed = folded && !navbar.foldedOpen;
  const foldedAndOpened = folded && navbar.foldedOpen;

  const isTouchScreen = useMemo(() => {
    return 'ontouchstart' in window;
  }, [window]);

  // Reference: http://dangkhoahome.blogspot.com/2013/05/kiem-tra-1-iem-co-nam-trong-giac-loi.html
  function pnpoly(nvert, vertx, verty, testx, testy) {
    let c = false;
    for (let i = 0, j = nvert - 1; i < nvert; j = i++) {
      if (
        verty[i] > testy !== verty[j] > testy &&
        testx < ((vertx[j] - vertx[i]) * (testy - verty[i])) / (verty[j] - verty[i]) + vertx[i]
      )
        c = !c;
    }
    return c;
  }

  function toggleNav() {
    hoverOpenRef.current = false;
    if (childNavRef.current) {
      childNavRef.current.style.width = 'inherit';
      childNavRef.current.style.minWidth = 'inherit';
    }

    dispatch(Actions.navbarCloseFolded());
  }

  useEffect(() => {
    dispatch(Actions.getCollapseNavbar());
  }, []);

  useEffect(() => {
    // In some screen, browser may freeze because of heavy render, it may lost event onMouseLeave on navigation
    // so navigation will not close although mouse had moved out navigation.
    // Solution: will check current mouse postion. If mouse has moved out the navigation, we will trigger close navigation.
    const closeNav = throttle((ev) => {
      if (!navRef.current?.getBoundingClientRect || !navRef.current?.querySelector) return;

      const {
        top: navTop,
        bottom: navBottom,
        left: navLeft,
        right: navRight,
      } = navRef.current?.getBoundingClientRect(); // Main nav
      const {
        top: subTop,
        bottom: subBottom,
        left: subLeft,
        right: subRight,
      } = navRef.current?.querySelector(`#sub-nav-${navBarStateRef.current.subNavOpen}`)?.getBoundingClientRect() || {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
      }; // Sub nav

      let mainPadWidth = 0;
      if (navBarStateRef.current.foldedOpen) mainPadWidth = navbarWidth;

      const isInMainNav = pnpoly(
        4,
        [navLeft, navRight + mainPadWidth, navRight + mainPadWidth, navLeft],
        [navTop, navTop, navBottom, navBottom],
        ev.pageX,
        ev.pageY,
      );

      const inSubNav = pnpoly(
        4,
        [subLeft, subRight + navbarWidth, subRight + navbarWidth, subLeft],
        [subTop, subTop, subBottom, subBottom],
        ev.pageX,
        ev.pageY,
      );

      if (navBarStateRef.current.keepCollapsed && navBarStateRef.current.foldedOpen) {
        dispatch(Actions.navbarCloseSub());
        window.requestAnimationFrame(toggleNav);
        return;
      }

      if (!navBarStateRef.current.subNavOpen && !isInMainNav && navBarStateRef.current.foldedOpen) {
        window.requestAnimationFrame(toggleNav);
      }

      if (navBarStateRef.current.subNavOpen && !inSubNav && !isInMainNav && navBarStateRef.current.foldedOpen) {
        dispatch(Actions.navbarCloseSub());
        window.requestAnimationFrame(toggleNav);
      }
    }, 100);

    document.addEventListener('mousemove', closeNav);

    return () => {
      document.removeEventListener('mousemove', closeNav);
    };
  }, []);

  useEffect(() => {
    navBarStateRef.current = navbar;
    if (navbar.keepCollapsed) hoverOpenRef.current = false;
  }, [navbar]);

  const isExpanded = foldedAndOpened;
  const toggleLabel = navbar.keepCollapsed ? EXPAND_ON_HOVER : KEEP_COLLAPSED;

  const handleEdgeToggle = useMemo(
    () =>
      debounce(
        (event) => {
          event.preventDefault();
          event.stopPropagation();

          const isMenuExpanded = Boolean(navBarStateRef.current.foldedOpen);
          const isKeepCollapsed = Boolean(navBarStateRef.current.keepCollapsed);

          if (isMenuExpanded || !isKeepCollapsed) {
            hoverOpenRef.current = false;
            if (childNavRef.current) {
              childNavRef.current.style.width = 'inherit';
              childNavRef.current.style.minWidth = 'inherit';
            }
            navBarStateRef.current = {
              ...navBarStateRef.current,
              foldedOpen: false,
              subNavOpen: null,
              keepCollapsed: true,
            };
            dispatch(Actions.navbarCloseSub());
            dispatch(Actions.navbarCloseFolded());
            dispatch(Actions.setNavbarKeepCollapsed(true));
            return;
          }

          hoverOpenRef.current = false;
          navBarStateRef.current = { ...navBarStateRef.current, keepCollapsed: false, foldedOpen: false };
          dispatch(Actions.setNavbarKeepCollapsed(false));
          dispatch(Actions.navbarCloseFolded());
        },
        TOGGLE_CLICK_WAIT_MS,
        { leading: true, trailing: false },
      ),
    [dispatch],
  );

  useEffect(() => () => handleEdgeToggle.cancel(), [handleEdgeToggle]);

  const isPointerOnEdgeToggle = (event) => {
    if (event?.target?.closest?.('[data-navbar-edge-toggle]')) return true;
    const underPointer = document.elementFromPoint(event.clientX, event.clientY);
    return Boolean(underPointer?.closest?.('[data-navbar-edge-toggle]'));
  };

  const openOnHover = (event) => {
    if (isPointerOnEdgeToggle(event)) return;
    if (!folded || navBarStateRef.current.keepCollapsed || hoverOpenRef.current) return;
    hoverOpenRef.current = true;
    dispatch(Actions.navbarOpenFolded());
  };

  const stopToggleHover = (event) => {
    event.stopPropagation();
  };

  return (
    <>
      <ThemeProvider theme={navbarTheme}>
        <div id="fuse-navbar" ref={navRef} className={clsx(classes.wrapper, folded && classes.wrapperFolded)}>
          {/* <Hidden mdDown> */}
          <div
            ref={childNavRef}
            className={clsx(
              classes.navbar,
              classes[config.navbar.position],
              folded && classes.folded,
              foldedAndOpened && classes.foldedAndOpened,
              foldedAndClosed && classes.foldedAndClosed,
              navbar.collapseNav ? classes.collapseItemText : classes.noneCollapse,
            )}
            onMouseEnter={openOnHover}
            onMouseMove={openOnHover}
            onMouseLeave={() => {
              hoverOpenRef.current = false;
              if (foldedAndOpened || navBarStateRef.current.foldedOpen) dispatch(Actions.navbarCloseFolded());
            }}
            style={{
              backgroundColor: navbarTheme.palette.background.default,
              transitionDelay: prevNavbar?.subNavOpen
                ? `${isTouchScreen ? 0 : theme.transitions.duration.standard}ms`
                : '0ms',
            }}
          >
            <NavbarLayout1 className={classes.navbarContent} />
            {folded && (
              <button
                type="button"
                className={classes.edgeToggle}
                data-navbar-edge-toggle=""
                aria-label={toggleLabel}
                aria-expanded={isExpanded}
                aria-pressed={navbar.keepCollapsed}
                onMouseEnter={stopToggleHover}
                onMouseMove={stopToggleHover}
                onClick={handleEdgeToggle}
              >
                <span
                  className={clsx(
                    classes.toggleSpin,
                    isExpanded ? classes.toggleIconExpanded : classes.toggleIconCollapsed,
                  )}
                >
                  <span className={clsx(classes.arrowGroup, !navbar.keepCollapsed && classes.arrowGroupDouble)}>
                    <Icon color="#ffffff" icon="chevron-right" size={0.85} width={0.85} className={classes.arrowIcon} />
                    <span className={clsx(classes.arrowExtra, !navbar.keepCollapsed && classes.arrowExtraVisible)}>
                      <Icon
                        color="#ffffff"
                        icon="chevron-right"
                        size={0.85}
                        width={0.85}
                        aria-hidden
                        className={classes.arrowIcon}
                      />
                    </span>
                  </span>
                </span>
              </button>
            )}
          </div>
        </div>
      </ThemeProvider>

      {config.navbar.display && !config.toolbar.display && (
        <Hidden lgUp>
          <NavbarMobileToggleFab />
        </Hidden>
      )}
    </>
  );
}

export default NavbarWrapperLayout1;
